/**
 * iOS 26: a navigation screen's root list or vertical scroll view starts at
 * the top of the screen and its content scrolls under the bar; a large title
 * scrolls with it. The engine insets the scroll view's content by the bar
 * (and the title row) and marks it with a `topInset` chrome rect, plus a
 * `largeTitle` rect when the title scrolls with it (layout/engine.ts,
 * `placeNavScreen`).
 *
 * The title element moves into the scroll view (an absolutely positioned
 * child, which the browser scrolls with the content), so it moves in step
 * with the content however fast it scrolls, overscroll included. Scrolling
 * only toggles flags: `data-sb-scrolled` on the screen once content is under
 * the bar (its scroll edge effect, styles.css) and `data-sb-collapsed` once
 * the title has gone under it, which shows the inline title. A list or
 * scroll view deeper in the screen's content whose top touches the bar runs
 * under it too (without the title). Its `data-sb-edge-top`
 * (`.scrollEdgeEffectStyle` / `.scrollEdgeEffectHidden`, scrollEdge.ts) is
 * copied to the screen, whose bar draws the effect.
 */

import type { Rect } from './layout';
import { edgeStrength, setProperty, setValue } from './scrollEdge';
import { runScrollSync, scrollTopOf, type ScrollSync } from './scrollSync';

/** The scroll offset past which the title has gone under the bar. */
const COLLAPSE_OFFSET = 44;

const bound = new WeakSet<HTMLElement>();
/** Each screen's large title element, wherever it currently is. */
const titles = new WeakMap<HTMLElement, HTMLElement>();

/**
 * A screen's large title element: in the screen's body, or in the scroll
 * view that carries it (put back in the body if that scroll view is gone).
 */
export function largeTitleOf(screen: HTMLElement): HTMLElement | null {
  let title = titles.get(screen) ?? screen.querySelector<HTMLElement>(':scope > .sb-navscreen-body > .sb-navbar-large-title');
  if (!title) return null;
  titles.set(screen, title);
  const body = screen.querySelector<HTMLElement>(':scope > .sb-navscreen-body');
  if (body && !screen.contains(title.parentElement)) body.prepend(title);
  return title;
}

/**
 * The large title's text: a span of its own, as the title element also
 * holds a `.largeTitle` toolbar (which then hides the text, styles.css).
 */
export function largeTitleTextOf(title: HTMLElement): HTMLElement {
  let text = title.querySelector<HTMLElement>(':scope > .sb-navbar-large-title-text');
  if (!text) {
    text = document.createElement('span');
    text.className = 'sb-navbar-large-title-text';
    title.prepend(text);
  }
  return text;
}

/** Whether a screen's large title currently scrolls inside one of its scroll views. */
export function largeTitleScrolls(screen: HTMLElement, title: HTMLElement): boolean {
  return title.parentElement !== null && !title.parentElement.classList.contains('sb-navscreen-body') && screen.contains(title);
}

function screenOf(scroller: HTMLElement): HTMLElement | null {
  return scroller.closest<HTMLElement>('.sb-navscreen');
}

/**
 * Called for every list and scroll view the layout reaches with its frame and
 * the engine's `topInset` / `largeTitle` rects (absent when it is not its
 * screen's root scroller).
 */
export function bindScrollUnderBar(scroller: HTMLElement, frame: Rect, topInset: Rect | undefined, titleRect: Rect | undefined): void {
  const screen = screenOf(scroller);
  if (topInset === undefined) {
    if (scroller.dataset['sbUnderBar'] !== undefined) {
      delete scroller.dataset['sbUnderBar'];
      scroller.style.removeProperty('--sb-top-inset');
      if (screen) {
        delete screen.dataset['sbScrolled'];
        delete screen.dataset['sbCollapsed'];
        delete screen.dataset['sbEdgeTop'];
        screen.style.removeProperty('--sb-edge-top');
      }
    }
    if (screen) releaseTitle(screen, scroller);
    return;
  }
  scroller.dataset['sbUnderBar'] = '';
  scroller.style.setProperty('--sb-top-inset', `${topInset.height}px`);
  if (screen) {
    setValue(screen, 'sbEdgeTop', scroller.dataset['sbEdgeTop'] ?? null);
    const title = largeTitleOf(screen);
    if (title && titleRect) {
      if (title.parentElement !== scroller) scroller.append(title);
      const s = title.style;
      s.left = `${titleRect.x - frame.x}px`;
      s.top = `${titleRect.y - frame.y}px`;
      s.width = `${titleRect.width}px`;
      s.height = `${titleRect.height}px`;
    } else {
      releaseTitle(screen, scroller);
    }
  }
  if (!bound.has(scroller)) {
    bound.add(scroller);
    scroller.addEventListener('scroll', () => syncScrollUnderBar(scroller), { passive: true });
  }
  syncScrollUnderBar(scroller);
}

/** Put a title this scroll view carries back in its screen's body. */
function releaseTitle(screen: HTMLElement, scroller: HTMLElement): void {
  const title = titles.get(screen);
  if (!title || title.parentElement !== scroller) return;
  screen.querySelector<HTMLElement>(':scope > .sb-navscreen-body')?.prepend(title);
}

export function syncScrollUnderBar(scroller: HTMLElement): void {
  runScrollSync(scrollUnderBarSync(scroller));
}

/** The sync as a read (`scrollTop`) and the writes from it (scrollSync.ts). */
function scrollUnderBarSync(scroller: HTMLElement): ScrollSync<number | null> {
  return {
    measure: () => (scroller.dataset['sbUnderBar'] === undefined || !screenOf(scroller) ? null : scrollTopOf(scroller)),
    apply: (offset) => {
      const screen = screenOf(scroller);
      if (offset === null || !screen) return;
      setFlag(screen, 'sbScrolled', offset > 0);
      // The edge effect's strength ramps up over the first `EDGE_RAMP` px.
      setProperty(screen, '--sb-edge-top', edgeStrength(offset));
      const title = titles.get(screen);
      const carries = title !== undefined && title.parentElement === scroller;
      setFlag(screen, 'sbCollapsed', carries && offset > COLLAPSE_OFFSET);
    },
  };
}

function setFlag(el: HTMLElement, key: string, on: boolean): void {
  if (on) {
    if (el.dataset[key] === undefined) el.dataset[key] = '';
  } else if (el.dataset[key] !== undefined) {
    delete el.dataset[key];
  }
}
